import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  isDevMode,
  OnInit,
  output,
  PLATFORM_ID,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, Subject, take, tap, throttleTime } from 'rxjs';
import { MasonryLayoutEvent, MasonryOptions } from './masonry.models';
import { APP_MASONRY, fromResize, ItemPosition, MasonryContainer, MasonryItem } from './masonry.token';

@Component({
  selector: 'app-masonry',
  template: '<ng-content />',
  styles: `
    app-masonry {
      display: block;
      position: relative;
    }
  `,
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: APP_MASONRY, useExisting: AppMasonryComponent }],
  host: { '[style.height]': 'containerHeight()', '[attr.data-layout-count]': 'layoutCount()' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppMasonryComponent implements OnInit, MasonryContainer {
  // DI
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly platformId = inject(PLATFORM_ID);

  // Inputs
  readonly options = input<MasonryOptions>({});
  readonly ordered = input(false);

  // Outputs
  readonly layoutComplete = output<MasonryLayoutEvent>();
  readonly removeComplete = output<Element>();
  readonly itemsLoaded = output<number>();

  // Host bindings
  protected readonly containerHeight = signal<string>('');
  protected readonly layoutCount = signal<number>(0);

  // State
  private readonly items = new Map<MasonryItem, 'pending' | 'ready' | 'active'>();
  private readonly itemByEl = new WeakMap<Element, MasonryItem>();
  private lastPositions = new Map<MasonryItem, ItemPosition>();
  private initialized = false;

  // Internal streams
  private readonly layout$ = new Subject<void>();
  private readonly resize$ = new Subject<void>();

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.layout$
      .pipe(
        tap(() => isDevMode() && console.log('Masonry: layout scheduled', this.items.size)),
        throttleTime(30, undefined, { leading: true, trailing: true }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.layout());

    this.resize$
      .pipe(
        tap(() => isDevMode() && console.log('Masonry: resize', this.items.size)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.layout$.next());

    afterNextRender(
      () => {
        if (isDevMode()) console.log('Masonry: initialized', this.items.size);
        this.initialized = true;
        this.processPending();
        this.layout$.next();

        const mo = new MutationObserver(mutations => {
          for (const m of mutations) {
            for (const node of m.removedNodes) {
              const item = this.itemByEl.get(node as Element);
              if (item) {
                this.items.delete(item);
                this.itemByEl.delete(node as Element);
                this.layout$.next();
                this.removeComplete.emit(item.element());
              }
            }
          }
        });
        mo.observe(this.el.nativeElement, { childList: true, subtree: true });
        this.destroyRef.onDestroy(() => mo.disconnect());

        fromResize(this.el.nativeElement)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.resize$.next());
      },
      { injector: this.injector }
    );

    this.destroyRef.onDestroy(() => this.items.clear());
  }

  register(item: MasonryItem): void {
    this.items.set(item, 'pending');
    this.itemByEl.set(item.element(), item);
    item
      .ready$()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.ordered()) {
          this.items.set(item, 'ready');
          this.processPending();
        } else {
          this.items.set(item, 'active');
        }
        this.layout$.next();
        item
          .resize$()
          .pipe(
            filter(() => this.initialized),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe(() => this.resize$.next());
      });
  }

  unregister(item: MasonryItem): void {
    this.items.delete(item);
    this.itemByEl.delete(item.element());
    this.lastPositions.delete(item);
    this.layout$.next();
    this.removeComplete.emit(item.element());
  }

  layout(): void {
    if (!this.initialized) return;
    const orderedItems = [...this.items.entries()]
      .filter(([, status]) => status === 'active')
      .map(([item]) => item)
      .sort((a, b) => a.comparePosition(b));

    const options = this.options();
    const containerWidth = this.innerWidth();

    if (containerWidth === 0 || orderedItems.length === 0) {
      const changed = this.lastPositions.size > 0 || this.containerHeight() !== '0px';
      if (changed) {
        this.containerHeight.set('0px');
        this.lastPositions = new Map();
        this.layoutCount.update(n => n + 1);
      }
      if (isDevMode()) console.log('Masonry: layout', changed ? 'changed' : 'unchanged', orderedItems.length);
      this.layoutComplete.emit({ items: orderedItems.map(i => i.element()), changed });
      return;
    }

    const gutter = this.resolveSize(options.gutter);
    let colWidth = this.resolveSize(options.columnWidth);

    // batch-read all dimensions before any writes
    const dims = orderedItems.map(i => i.dimensions());

    if (!colWidth) colWidth = dims[0]?.width || containerWidth;
    if (!colWidth) return;

    const { cols, colWidthWithGutter } = this.computeColumns(containerWidth, colWidth, gutter);
    const originLeft = options.originLeft !== false;
    const percentPosition = options.percentPosition ?? false;

    const colYs = new Array<number>(cols).fill(0);
    let horizontalColIndex = 0;
    let changed = this.lastPositions.size !== orderedItems.length;
    const newPositions = new Map<MasonryItem, ItemPosition>();

    for (let i = 0; i < orderedItems.length; i++) {
      const item = orderedItems[i];
      const itemW = dims[i].width;
      const itemH = dims[i].height;
      const colSpan = this.computeColSpan(itemW, colWidthWithGutter, cols);
      const col = options.horizontalOrder
        ? this.findColumnHorizontal(cols, colSpan, horizontalColIndex)
        : this.findColumnVertical(colYs, colSpan);

      if (options.horizontalOrder && itemW && itemH) horizontalColIndex = col + colSpan;

      const x = colWidthWithGutter * col;
      let y = colYs[col];
      if (colSpan > 1) for (let k = col + 1; k < col + colSpan; k++) if (colYs[k] > y) y = colYs[k];

      const pos: ItemPosition = {
        x: percentPosition ? `${(x / containerWidth) * 100}%` : `${x}px`,
        y: `${y}px`,
        originLeft,
      };
      newPositions.set(item, pos);
      item.applyPosition(pos);

      if (!changed) {
        const prev = this.lastPositions.get(item);
        if (prev?.x !== pos.x || prev.y !== pos.y) changed = true;
      }

      const bottom = y + itemH;
      for (let j = col; j < col + colSpan; j++) colYs[j] = bottom;
    }

    const newHeight = `${this.maxColY(colYs)}px`;
    if (newHeight !== this.containerHeight()) changed = true;

    this.lastPositions = newPositions;
    if (changed) {
      this.containerHeight.set(newHeight);
      this.layoutCount.update(n => n + 1);
    }
    if (isDevMode()) console.log('Masonry: layout', changed ? 'changed' : 'unchanged', orderedItems.length);
    this.layoutComplete.emit({ items: orderedItems.map(i => i.element()), changed });
  }

  private computeColumns(
    containerWidth: number,
    colWidth: number,
    gutter: number
  ): { cols: number; colWidthWithGutter: number } {
    const colWidthWithGutter = colWidth + gutter;
    const containerWidthWithGutter = containerWidth + gutter;
    const rawCols = containerWidthWithGutter / colWidthWithGutter;
    const excess = colWidthWithGutter - (containerWidthWithGutter % colWidthWithGutter);
    const cols = Math.max(1, (excess && excess < 1 ? Math.round : Math.floor)(rawCols));
    return { cols, colWidthWithGutter };
  }

  private computeColSpan(itemW: number, colWidthWithGutter: number, cols: number): number {
    const remainder = itemW % colWidthWithGutter;
    return Math.min(cols, (remainder && remainder < 1 ? Math.round : Math.ceil)(itemW / colWidthWithGutter));
  }

  private findColumnVertical(colYs: number[], colSpan: number): number {
    const groupCount = colYs.length + 1 - colSpan;
    let minY = Infinity,
      col = 0;
    for (let j = 0; j < groupCount; j++) {
      let groupY = colYs[j];
      if (colSpan > 1) for (let k = j + 1; k < j + colSpan; k++) if (colYs[k] > groupY) groupY = colYs[k];
      if (groupY < minY) {
        minY = groupY;
        col = j;
      }
    }
    return col;
  }

  private findColumnHorizontal(cols: number, colSpan: number, horizontalColIndex: number): number {
    let col = horizontalColIndex % cols;
    if (colSpan > 1 && col + colSpan > cols) col = 0;
    return col;
  }

  private maxColY(colYs: number[]): number {
    let max = 0;
    for (const colY of colYs) if (colY > max) max = colY;
    return max;
  }

  private innerWidth(): number {
    const el = this.el.nativeElement;
    const s = getComputedStyle(el);
    return (
      el.offsetWidth -
      parseFloat(s.paddingLeft) -
      parseFloat(s.paddingRight) -
      parseFloat(s.borderLeftWidth) -
      parseFloat(s.borderRightWidth)
    );
  }

  private resolveSize(value: number | string | undefined): number {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    const el = this.el.nativeElement.querySelector<HTMLElement>(value);
    if (!el) return 0;
    const s = getComputedStyle(el);
    return el.offsetWidth + parseFloat(s.marginLeft) + parseFloat(s.marginRight);
  }

  private processPending(): void {
    const entries = [...this.items.entries()];
    const blocked = entries.findIndex(([, status]) => status === 'pending');
    const toActivate =
      blocked === -1
        ? entries.filter(([, s]) => s === 'ready')
        : entries.slice(0, blocked).filter(([, s]) => s === 'ready');
    for (const [item] of toActivate) {
      this.items.set(item, 'active');
    }
    if (toActivate.length) this.itemsLoaded.emit([...this.items.values()].filter(s => s === 'active').length);
  }
}
